import React from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { controlBoxClasses } from "../../../components/ui";
import { normaliseExperienceInput } from "../validation";

interface ExperienceFieldProps {
  /**
   * The unit this figure is counted in, painted after the number and used as
   * the control's accessible name: the pair sits under one caption, and
   * "Work experience" twice would name neither of them.
   */
  unit: "years" | "months";
  /** Set where something outside has to reach the input — see `controlId`. */
  id?: string;
  value: string;
  onValueChange: (value: string) => void;
  onBlur?: () => void;
  error?: string;
  /** The highest the arrows will climb to. The typed value is the rules' own. */
  max: number;
}

/** Digits only, and never below nothing — a negative experience is a typo. */
const stepped = (value: string, by: number, max: number): string => {
  const next = (Number(value) || 0) + by;
  return String(Math.min(Math.max(next, 0), max));
};

/**
 * One half of the Work experience row: a number, the unit it is counted in,
 * and a pair of arrows to nudge it.
 *
 * Its own control rather than a `TextField` with `type="number"`, for what the
 * design asks of it: the unit is painted inside the box after the value, and
 * the browser's own spinner would sit between the two. So the box comes from
 * the shared kit — `controlBoxClasses`, the same border and height every other
 * row wears — and what goes inside it is drawn here.
 *
 * The input stays `text` with a numeric keypad. `type="number"` accepts `e`,
 * `+` and `-` as partial input, silently reports an empty string for anything
 * it cannot parse, and scrolls its own value under the wheel — none of which a
 * field counting years has any use for.
 */
export const ExperienceField: React.FC<ExperienceFieldProps> = ({
  unit,
  id,
  value,
  onValueChange,
  onBlur,
  error,
  max,
}) => {
  const generatedId = React.useId();
  const fieldId = id ?? generatedId;
  const errorId = `${fieldId}-error`;
  const label = `Work experience in ${unit}`;

  const step = (by: number) => () =>
    onValueChange(stepped(value, by, max));

  return (
    <div>
      <div className={controlBoxClasses(!!error)}>
        {/* Sized to the two digits it can hold rather than stretched, so the
            unit sits against the number the way a written figure does. */}
        <input
          id={fieldId}
          value={value}
          inputMode="numeric"
          aria-label={label}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          placeholder="0"
          onChange={(event) =>
            onValueChange(normaliseExperienceInput(event.target.value))
          }
          onBlur={onBlur}
          // The arrows are on the control itself too, so the keyboard can nudge
          // it without reaching for them.
          onKeyDown={(event) => {
            if (event.key === "ArrowUp") {
              event.preventDefault();
              onValueChange(stepped(value, 1, max));
            }
            if (event.key === "ArrowDown") {
              event.preventDefault();
              onValueChange(stepped(value, -1, max));
            }
          }}
          className="w-8 min-w-0 bg-transparent text-inherit placeholder:text-ink-subtle focus:outline-none"
        />
        <span aria-hidden="true" className="select-none text-ink-muted">
          {unit}
        </span>

        {/* Stacked at the far end of the box, where a native spinner would be.
            Hidden from screen readers: they are a second way to type a number
            that is already in the input beside them, and the input says how it
            is stepped through its own arrow keys. */}
        <span aria-hidden="true" className="ml-auto flex flex-col">
          <button
            type="button"
            tabIndex={-1}
            onClick={step(1)}
            className="flex h-3 w-4 items-center justify-center text-ink-subtle hover:text-ink transition-colors"
          >
            <ChevronUp className="h-3 w-3" />
          </button>
          <button
            type="button"
            tabIndex={-1}
            onClick={step(-1)}
            className="flex h-3 w-4 items-center justify-center text-ink-subtle hover:text-ink transition-colors"
          >
            <ChevronDown className="h-3 w-3" />
          </button>
        </span>
      </div>

      {error && (
        <p id={errorId} className="mt-1.5 text-[11px] 3xl:text-[12px] text-red-500">
          {error}
        </p>
      )}
    </div>
  );
};

export default ExperienceField;
